// src/components/communication/QuickProductViewModal.js
import React, { useState, useEffect } from "react";
import {
  X,
  Sparkles,
  DollarSign,
  Tag,
  ExternalLink,
  Loader,
  AlertCircle,
  Gem,
  Package,
  Boxes,
  PackageCheck,
  Factory,
  Bookmark,
  RefreshCw,
  Flame,
  Plus,
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { useCommunication } from "../../contexts/CommunicationContext";
import { useCatalogContext } from "../../contexts/CatalogContext";
import { formatMoney } from "../../utils/formatters";
import {
  cleanSku,
  getBaseSku,
  isModeloComMedida,
  resolveModeloJoia,
  extractAroFromSku,
} from "../../utils/skuUtils";
import EnviarCerasFundicaoModal from "../modals/EnviarCerasFundicaoModal";

export default function QuickProductViewModal({ onNavigateToProduct, user }) {
  const { quickProductSku, closeQuickProduct, openLightbox } = useCommunication();
  const { findItem, catalog, isMoldeSku, getSubestoqueCera } = useCatalogContext() || {};
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [fundirModalItem, setFundirModalItem] = useState(null);

  // Estado de variações (filhos) e estoques
  const [variations, setVariations] = useState([]);
  const [variationsTotals, setVariationsTotals] = useState({ physical: 0, production: 0, reserved: 0 });
  const [loadingVariations, setLoadingVariations] = useState(false);

  useEffect(() => {
    if (!quickProductSku) {
      setProduct(null);
      setError(null);
      setVariations([]);
      return;
    }

    const fetchProduct = async () => {
      setLoading(true);
      setError(null);

      const targetSku = cleanSku(quickProductSku);

      let foundProduct = null;

      // 1. Tenta buscar da memória rápida do CatalogContext primeiro
      if (findItem) {
        const memoryItem = findItem(targetSku);
        if (memoryItem) {
          foundProduct = {
            sku: memoryItem.sku || targetSku,
            name: memoryItem.name || memoryItem.title || "Produto sem nome",
            image: memoryItem.image || memoryItem.image_url || memoryItem.image_default || "",
            price: Number(memoryItem.price || memoryItem.preco || 0),
            category: memoryItem.category || memoryItem.categoria || "Geral",
            stoneColor: memoryItem.stoneColor || memoryItem.cor_pedra || "",
            model: memoryItem.model || memoryItem.modelo || "",
            status: memoryItem.status || "active",
          };
        }
      }

      // 2. Busca no PocketBase (tabela catalog ou fichas_tecnicas)
      if (!foundProduct) {
        try {
          const parts = targetSku.split("-");
          const permuted = parts.length >= 2 ? `${parts[1]}-${parts[0]}` : "";
          const filterStr = permuted
            ? `sku = "${targetSku}" || sku ~ "${targetSku}" || sku ~ "${permuted}"`
            : `sku = "${targetSku}" || sku ~ "${targetSku}"`;

          const records = await pb.collection("catalog").getList(1, 1, {
            filter: filterStr,
            sort: "-created",
          });

          if (records.items && records.items.length > 0) {
            const raw = records.items[0];
            const full = raw.full_data || {};

            let imageUrl = raw.image_url || full.image_default || full.image || "";
            if (imageUrl && !imageUrl.startsWith("http") && full.id) {
              imageUrl = `https://cdn.iset.io/assets/34692/produtos/${full.id}/${imageUrl}`;
            }

            foundProduct = {
              sku: raw.sku || full.sku || targetSku,
              name: raw.name || full.name || full.title || full.nome || "Joia Sem Nome",
              image: imageUrl,
              price: Number(full.price || raw.price || 0),
              category: full.category || full.categoria || "Joias",
              stoneColor: full.stoneColor || full.cor || "",
              model: full.model || full.modelo || "",
              status: full.status || "active",
            };
          } else {
            // Fallback: busca em fichas_tecnicas
            const ficha = await pb.collection("fichas_tecnicas").getFirstListItem(`sku = "${targetSku}" || sku ~ "${permuted || targetSku}"`).catch(() => null);
            if (ficha) {
              foundProduct = {
                sku: ficha.sku || targetSku,
                name: ficha.nome || ficha.sku || "Ficha Técnica",
                image: ficha.foto || "",
                price: Number(ficha.preco_sugerido || 0),
                category: "Fábrica / Ficha Técnica",
                stoneColor: "",
                model: "",
                status: "active",
              };
            } else {
              setError(`Produto com SKU "${targetSku}" não localizado no catálogo.`);
            }
          }
        } catch (err) {
          console.error("Erro ao buscar produto por SKU:", err);
          setError("Erro ao carregar dados do produto.");
        }
      }

      setProduct(foundProduct);
      setLoading(false);

      // 3. Busca Variações (Aros/Filhos) e seus Estoques Físicos
      const baseSku = foundProduct?.sku ? getBaseSku(cleanSku(foundProduct.sku)) : getBaseSku(targetSku);
      if (baseSku) {
        fetchVariationsAndStock(baseSku);
      }
    };

    fetchProduct();
  }, [quickProductSku, findItem]);

  // Função para buscar estoque das variações
  const fetchVariationsAndStock = async (targetBaseSku) => {
    if (!targetBaseSku) return;
    setLoadingVariations(true);

    try {
      const parts = targetBaseSku.split("-");
      const permuted = parts.length >= 2 ? `${parts[1]}-${parts[0]}` : "";

      const invFilter = permuted
        ? `sku ~ "${targetBaseSku}" || baseSku ~ "${targetBaseSku}" || sku ~ "${permuted}" || baseSku ~ "${permuted}"`
        : `sku ~ "${targetBaseSku}" || baseSku ~ "${targetBaseSku}"`;

      const resFilter = permuted
        ? `(sku ~ "${targetBaseSku}" || sku ~ "${permuted}") && status = "PENDENTE"`
        : `sku ~ "${targetBaseSku}" && status = "PENDENTE"`;

      const [stockRes, resRes] = await Promise.all([
        pb.collection("inventory_items").getFullList({
          filter: invFilter,
          sort: "-created",
        }).catch(() => []),
        pb.collection("reservations").getFullList({
          filter: resFilter,
          sort: "-created",
        }).catch(() => []),
      ]);

      // Identifica o tipo/modelo da peça para tratamento de numerações (Aros)
      const catItem = (catalog && (catalog[targetBaseSku] || Object.values(catalog).find((i) => cleanSku(i.sku) === targetBaseSku))) || null;
      const modeloNome = resolveModeloJoia(targetBaseSku, catItem, null);
      const isAnel = isModeloComMedida(modeloNome);

      const targetCera = getSubestoqueCera ? getSubestoqueCera(targetBaseSku) : null;

      const variationSkusSet = new Set();
      variationSkusSet.add(targetBaseSku);

      // Mapeia SKUs do catálogo que pertencem a este SKU Pai
      if (catalog) {
        Object.values(catalog).forEach((item) => {
          const itemSku = cleanSku(item.sku || item.full_data?.sku);
          if (
            itemSku &&
            (getBaseSku(itemSku) === targetBaseSku || (permuted && getBaseSku(itemSku) === permuted) || itemSku.startsWith(targetBaseSku))
          ) {
            variationSkusSet.add(itemSku);
          }
        });
      }

      // Mapeia variações existentes no estoque físico
      stockRes.forEach((item) => {
        const itemSku = cleanSku(item.sku);
        if (
          itemSku &&
          (getBaseSku(itemSku) === targetBaseSku || (permuted && getBaseSku(itemSku) === permuted) || itemSku.startsWith(targetBaseSku))
        ) {
          variationSkusSet.add(itemSku);
        }
      });

      // Se for anel e tiver aros disponíveis cadastrados no molde ou no estoque por aro
      if (isAnel) {
        if (targetCera?.estoque_por_aro) {
          Object.keys(targetCera.estoque_por_aro).forEach((aro) => {
            variationSkusSet.add(`${targetBaseSku}-${aro}`);
          });
        }
        if (Array.isArray(targetCera?.config_borracha?.aros_disponiveis)) {
          targetCera.config_borracha.aros_disponiveis.forEach((aro) => {
            variationSkusSet.add(`${targetBaseSku}-${aro}`);
          });
        }
      }

      // Ordenação natural de SKUs: aros numéricos em ordem crescente (ex: 12, 14, 16, 18...)
      const sortedSkus = Array.from(variationSkusSet).sort((a, b) => {
        const aroA = extractAroFromSku(a);
        const aroB = extractAroFromSku(b);
        if (aroA !== null && aroB !== null) return aroA - aroB;
        if (aroA !== null) return -1;
        if (aroB !== null) return 1;
        return a.localeCompare(b);
      });

      const rawList = sortedSkus.map((vSku) => {
        const physicalQty = stockRes.filter(
          (i) => cleanSku(i.sku) === vSku && i.status === "in_stock" && !i.isPE
        ).length;

        const productionQty = stockRes.filter(
          (i) => cleanSku(i.sku) === vSku && i.isPE && !["in_stock", "sold", "adjusted_out"].includes(i.status)
        ).length;

        const reservedQty = resRes
          .filter((r) => cleanSku(r.sku) === vSku)
          .reduce((sum, r) => sum + (Number(r.quantity) || 1), 0);

        const aro = extractAroFromSku(vSku);
        const parts = vSku.split("-");
        const lastPart = parts[parts.length - 1];
        const isNumericSize = /^\d+$/.test(lastPart);

        const sizeLabel = aro !== null
          ? `Aro ${aro}`
          : isNumericSize
          ? `Aro ${lastPart}`
          : vSku === targetBaseSku
          ? "Tamanho Padrão"
          : `Var. ${lastPart}`;

        return {
          sku: vSku,
          sizeLabel,
          aro,
          physicalQty,
          productionQty,
          reservedQty,
          hasAnyMovement: physicalQty > 0 || productionQty > 0 || reservedQty > 0,
        };
      });

      // REGRA DE OURO DE VARIAÇÕES:
      // 1. Para Anéis e Alianças (isAnel === true):
      //    A grade de aros é indispensável. Todos os aros são mantidos (mesmo zerados) para dar visibilidade da numeração.
      // 2. Para Peças Sem Medida (Escapulários, Pingentes, Medalhas, etc.):
      //    Não há variação de aro. Sufixos como "-Z" com estoque zero e sem movimentação são variações
      //    fantasmas e são filtradas para não poluir a tabela como falsos tamanhos.
      let list = rawList;
      if (!isAnel) {
        list = rawList.filter((item) => item.sku === targetBaseSku || item.hasAnyMovement);
        if (list.length === 0 && rawList.length > 0) {
          list = [rawList[0]];
        }
      }

      let totalPhysical = 0;
      let totalProduction = 0;
      let totalReserved = 0;

      list.forEach((item) => {
        totalPhysical += item.physicalQty;
        totalProduction += item.productionQty;
        totalReserved += item.reservedQty;
      });

      setVariations(list);
      setVariationsTotals({
        physical: totalPhysical,
        production: totalProduction,
        reserved: totalReserved,
      });
    } catch (err) {
      console.warn("Aviso ao carregar variações do modal rápido:", err);
    } finally {
      setLoadingVariations(false);
    }
  };

  if (!quickProductSku) return null;

  const cleanSkuStr = product ? cleanSku(product.sku) : (quickProductSku ? cleanSku(quickProductSku) : "");
  const cleanPai = cleanSkuStr ? getBaseSku(cleanSkuStr) : "";
  const hasMolde = isMoldeSku ? (isMoldeSku(cleanSkuStr) || isMoldeSku(cleanPai)) : false;
  const ceraInfo = getSubestoqueCera ? getSubestoqueCera(cleanPai || cleanSkuStr) : { cera_bruta: 0, cera_precravada: 0, localizacao_gaveta: "", estoque_por_aro: {} };
  const totalCeras = (ceraInfo?.cera_bruta || 0) + (ceraInfo?.cera_precravada || 0);

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh] animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 text-white shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-300">
              <Sparkles size={18} />
            </div>
            <div>
              <span className="font-bold text-sm text-white">Ficha Rápida do Produto</span>
              <p className="text-[10px] text-slate-400 font-mono">SKU: {quickProductSku}</p>
            </div>
          </div>

          <button
            onClick={closeQuickProduct}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* CORPO DO MODAL */}
        <div className="p-5 overflow-y-auto space-y-4 flex-1">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500 gap-3">
              <Loader className="animate-spin text-indigo-600" size={28} />
              <span className="text-xs font-medium">Buscando produto {quickProductSku}...</span>
            </div>
          )}

          {error && (
            <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 flex items-center gap-2.5 text-xs">
              <AlertCircle size={18} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {product && !loading && (
            <>
              {/* BLOCO PRINCIPAL: FOTO + DADOS BÁSICOS */}
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                {/* FOTO */}
                {product.image ? (
                  <div
                    onClick={() => openLightbox(product.image, product.name)}
                    className="relative group w-28 h-28 sm:w-32 sm:h-32 rounded-xl overflow-hidden border border-slate-200 bg-white cursor-pointer shadow-xs shrink-0 hover:shadow-md transition"
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-200"
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-[10px] font-bold">
                      Ampliar
                    </div>
                  </div>
                ) : (
                  <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                    <Package size={32} />
                  </div>
                )}

                {/* DETALHES COMERCIAIS */}
                <div className="flex-1 min-w-0 space-y-2 text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                    <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-md">
                      {product.sku}
                    </span>
                    <span className="text-[10px] font-bold text-slate-600 bg-white border border-slate-200 px-2 py-0.5 rounded-md">
                      {product.category || "Geral"}
                    </span>
                  </div>

                  <h3 className="text-sm font-bold text-slate-900 leading-snug">
                    {product.name}
                  </h3>

                  <div className="flex items-baseline justify-center sm:justify-start gap-2">
                    <span className="text-base font-black text-emerald-700">
                      {product.price > 0 ? formatMoney(product.price) : "Sob Consulta"}
                    </span>
                    {product.stoneColor && (
                      <span className="text-xs text-purple-700 font-medium">
                        • {product.stoneColor}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* SEÇÃO: MOLDE DE BORRACHA & SUBESTOQUE DE CERAS */}
              {(() => {
                const clean = product ? cleanSku(product.sku) : "";
                const cleanPai = clean ? getBaseSku(clean) : "";
                const hasMolde = isMoldeSku ? (isMoldeSku(clean) || isMoldeSku(cleanPai)) : false;
                const ceraInfo = getSubestoqueCera ? getSubestoqueCera(cleanPai || clean) : { cera_bruta: 0, cera_precravada: 0, localizacao_gaveta: "" };
                const totalCeras = (ceraInfo?.cera_bruta || 0) + (ceraInfo?.cera_precravada || 0);

                if (hasMolde) {
                  return (
                    <div className="p-3.5 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-amber-100/90 text-amber-800 border border-amber-200 shrink-0">
                          <Flame size={20} className="text-amber-600 animate-pulse" />
                        </div>
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-black text-amber-950 flex items-center gap-1">
                              ⚙️ Molde de Borracha Ativo
                            </span>
                            {ceraInfo.localizacao_gaveta && (
                              <span className="text-[10px] font-bold text-amber-800 bg-white border border-amber-200 px-2 py-0.5 rounded-md shadow-2xs">
                                📍 Gaveta: {ceraInfo.localizacao_gaveta}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-amber-800/90 font-medium">
                            Subestoque de Ceras: <strong className="font-black text-amber-950">{totalCeras} un</strong>{" "}
                            ({ceraInfo.cera_bruta || 0} brutas • {ceraInfo.cera_precravada || 0} pré-cravadas)
                          </p>

                          {/* Chips dos aros com ceras disponíveis */}
                          {ceraInfo.estoque_por_aro && Object.keys(ceraInfo.estoque_por_aro).length > 0 && (
                            <div className="flex items-center gap-1.5 flex-wrap pt-1">
                              <span className="text-[10px] font-bold text-amber-900/80">Por Aro:</span>
                              {Object.entries(ceraInfo.estoque_por_aro)
                                .filter(([_, q]) => (q.bruta > 0 || q.precravada > 0))
                                .sort((a, b) => (parseInt(a[0], 10) || 0) - (parseInt(b[0], 10) || 0))
                                .map(([aro, q]) => (
                                  <span
                                    key={aro}
                                    className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-white text-amber-950 border border-amber-300 shadow-2xs"
                                    title={`Aro ${aro}: ${q.bruta} brutas, ${q.precravada} pré-cravadas`}
                                  >
                                    {aro}: <strong className="text-amber-800">{q.bruta}b</strong>{q.precravada > 0 && <span className="text-purple-700 font-black">+{q.precravada}c</span>}
                                  </span>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setFundirModalItem({
                          sku: product.sku,
                          name: product.name,
                          image: product.image,
                          gaveta: ceraInfo.localizacao_gaveta,
                          cera_bruta: ceraInfo.cera_bruta || 0,
                          cera_precravada: ceraInfo.cera_precravada || 0,
                          estoque_por_aro: ceraInfo.estoque_por_aro || {}
                        })}
                        className="px-3.5 py-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white rounded-xl text-xs font-black shadow-sm shadow-amber-600/20 flex items-center gap-1.5 whitespace-nowrap cursor-pointer transition active:scale-95 shrink-0"
                        title="Solicitar produção de estoque para este molde (a fábrica decide o uso de cera da gaveta ou injeção)"
                      >
                        <Flame size={14} className="text-amber-200" />
                        <span>⚡ Pedir Estoque</span>
                      </button>
                    </div>
                  );
                }

                return (
                  <div className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1.5 font-medium text-[11px]">
                      <Package size={13} className="text-slate-400" /> Processo de Fábrica: <strong className="text-slate-700 font-bold">Impressão 3D / Resina</strong>
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">Sem molde de borracha</span>
                  </div>
                );
              })()}

              {/* SEÇÃO DE VARIAÇÕES (SKUS FILHOS / AROS) E ESTOQUE */}
              <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-2xs space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Boxes size={16} className="text-indigo-600" />
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                      Variações & Estoque Físico
                    </span>
                  </div>

                  {/* BADGES DE TOTAL */}
                  <div className="flex items-center gap-1.5 flex-wrap text-[11px] font-bold">
                    <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                      <PackageCheck size={12} />
                      Pronta Entrega: {variationsTotals.physical} un
                    </span>
                    {variationsTotals.production > 0 && (
                      <span className="px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200 flex items-center gap-1">
                        <Factory size={12} />
                        Fábrica: {variationsTotals.production} un
                      </span>
                    )}
                    {variationsTotals.reserved > 0 && (
                      <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                        <Bookmark size={12} />
                        Reservas: {variationsTotals.reserved} un
                      </span>
                    )}
                  </div>
                </div>

                {/* TABELA COMPACTA DE VARIAÇÕES */}
                {loadingVariations ? (
                  <div className="py-6 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                    <RefreshCw size={14} className="animate-spin text-indigo-600" />
                    <span>Consultando medidas e estoque...</span>
                  </div>
                ) : variations.length === 0 ? (
                  <div className="p-4 text-center text-xs text-slate-400 bg-slate-50 rounded-lg">
                    Nenhuma variação cadastrada para este modelo.
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="max-h-56 overflow-y-auto">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200 text-[10px] uppercase sticky top-0">
                          <tr>
                            <th className="px-3 py-2">Medida / Aro</th>
                            <th className="px-3 py-2">SKU Variação</th>
                            <th className="px-3 py-2 text-center">Pronta Entrega</th>
                            <th className="px-3 py-2 text-center">Fábrica</th>
                            <th className="px-3 py-2 text-center">Reservado</th>
                            {hasMolde && (
                              <th className="px-3 py-2 text-center text-amber-900 bg-amber-50/70 border-l border-amber-200">
                                🕯️ Ceras Gaveta
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {variations.map((v) => {
                            const hasPhysical = v.physicalQty > 0;
                            const hasProduction = v.productionQty > 0;
                            const hasReserved = v.reservedQty > 0;

                            const hasEstoquePorAro = Boolean(
                              ceraInfo?.estoque_por_aro && Object.keys(ceraInfo.estoque_por_aro).length > 0
                            );

                            const aroMatch = v.sizeLabel?.match(/\d+/) || v.sku?.match(/-(\d+)$/);
                            const aroKey = v.aro ? String(v.aro) : (aroMatch ? (aroMatch[1] || aroMatch[0]) : null);

                            let waxBruta = 0;
                            let waxPrecravada = 0;
                            let waxCount = 0;

                            if (hasEstoquePorAro && aroKey) {
                              const waxAro = ceraInfo.estoque_por_aro[aroKey];
                              waxBruta = Number(waxAro?.bruta) || 0;
                              waxPrecravada = Number(waxAro?.precravada) || 0;
                              waxCount = waxBruta + waxPrecravada;
                            } else if (!hasEstoquePorAro && (v.sku === cleanPai || v.sku === cleanSkuStr || variations.length === 1)) {
                              // Produtos sem grade de aros (Escapulários, Pingentes, etc.): o saldo da gaveta é exibido na linha principal
                              waxBruta = Number(ceraInfo?.cera_bruta) || 0;
                              waxPrecravada = Number(ceraInfo?.cera_precravada) || 0;
                              waxCount = waxBruta + waxPrecravada;
                            }

                            return (
                              <tr key={v.sku} className="hover:bg-slate-50 transition">
                                <td className="px-3 py-2 font-bold text-slate-800">
                                  {v.sizeLabel}
                                </td>
                                <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                                  {v.sku}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {hasPhysical ? (
                                    <span className="inline-block px-2 py-0.5 rounded font-bold bg-emerald-100 text-emerald-800 text-[11px]">
                                      {v.physicalQty} un
                                    </span>
                                  ) : (
                                    <span className="text-slate-300 font-mono">-</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {hasProduction ? (
                                    <span className="inline-block px-2 py-0.5 rounded font-bold bg-sky-100 text-sky-800 text-[11px]">
                                      {v.productionQty} un
                                    </span>
                                  ) : (
                                    <span className="text-slate-300 font-mono">-</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {hasReserved ? (
                                    <span className="inline-block px-2 py-0.5 rounded font-bold bg-amber-100 text-amber-800 text-[11px]">
                                      {v.reservedQty} un
                                    </span>
                                  ) : (
                                    <span className="text-slate-300 font-mono">-</span>
                                  )}
                                </td>
                                {hasMolde && (
                                  <td className="px-3 py-2 text-center bg-amber-50/20 border-l border-slate-100">
                                    {waxCount > 0 ? (
                                      <button
                                        type="button"
                                        onClick={() => setFundirModalItem({
                                          sku: v.sku || product.sku,
                                          name: product.name,
                                          image: product.image,
                                          gaveta: ceraInfo.localizacao_gaveta,
                                          cera_bruta: waxBruta,
                                          cera_precravada: waxPrecravada,
                                          estoque_por_aro: ceraInfo.estoque_por_aro || {},
                                          size: aroKey || (v.sizeLabel !== "Tamanho Padrão" ? v.sizeLabel : null),
                                          aro: aroKey || null,
                                        })}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-black bg-amber-100/90 hover:bg-amber-200 text-amber-950 border border-amber-300 text-[11px] shadow-2xs transition active:scale-95 cursor-pointer"
                                        title={aroKey ? `Aro ${aroKey}: ${waxBruta} brutas • ${waxPrecravada} pré-cravadas. Clique para solicitar produção!` : `${waxBruta} brutas • ${waxPrecravada} pré-cravadas. Clique para solicitar produção!`}
                                      >
                                        <Flame size={10} className="text-amber-700" />
                                        <span>{waxCount} un</span>
                                        {waxPrecravada > 0 && (
                                          <span className="text-[9px] text-purple-700 font-extrabold">({waxPrecravada}c)</span>
                                        )}
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => setFundirModalItem({
                                          sku: v.sku || product.sku,
                                          name: product.name,
                                          image: product.image,
                                          gaveta: ceraInfo.localizacao_gaveta,
                                          cera_bruta: 0,
                                          cera_precravada: 0,
                                          estoque_por_aro: ceraInfo.estoque_por_aro || {},
                                          size: aroKey || (v.sizeLabel !== "Tamanho Padrão" ? v.sizeLabel : null),
                                          aro: aroKey || null,
                                        })}
                                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-bold text-[10px] text-slate-400 hover:text-amber-700 hover:bg-amber-50 border border-transparent hover:border-amber-200 transition cursor-pointer"
                                        title={aroKey ? `Aro ${aroKey}: clique para solicitar produção!` : "Clique para solicitar produção deste produto!"}
                                      >
                                        <Plus size={9} />
                                        <span>Pedir</span>
                                      </button>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0">
          <button
            onClick={closeQuickProduct}
            className="px-3.5 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 transition cursor-pointer"
          >
            Fechar
          </button>

          {product && (
            <button
              onClick={() => {
                closeQuickProduct();
                if (onNavigateToProduct) {
                  onNavigateToProduct(product.sku);
                } else {
                  window.location.hash = `#produto/${product.sku}`;
                }
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-sm shadow-indigo-600/30 transition active:scale-95 cursor-pointer"
            >
              <span>Abrir Ficha do Produto Completa</span>
              <ExternalLink size={13} />
            </button>
          )}
        </div>
      </div>

      {/* MODAL DE ENVIO DE CERAS PARA FUNDIÇÃO */}
      {fundirModalItem && (
        <EnviarCerasFundicaoModal
          isOpen={!!fundirModalItem}
          onClose={() => setFundirModalItem(null)}
          item={fundirModalItem}
          user={user}
        />
      )}
    </div>
  );
}
